// Client/src/hooks/useArchitectHoursPreview.ts
// Debounced call to POST /billing/architect-hours/preview — shows the real Stripe-computed
// VAT/tax and total for an expert-hours purchase (plain hour block, or a fixed-price SKU like
// migration/production support) before the user pays, mirroring useCreditBundlePreview.ts.
// A separate hook rather than reusing that one directly since the request shape differs
// (hours/service_sku instead of credit_amount) — the debounce/gating/never-null-forever
// behavior is the same pattern.
import { useEffect, useRef, useState } from 'react';

export interface ArchitectHoursPreviewAddress {
  line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

/** Either a linear hour block, or a fixed-price service whose price the server resolves. */
export type ArchitectHoursPreviewTarget = { hours: number } | { sku: string };

export interface ArchitectHoursTaxBreakdownEntry {
  tax_type: string | null;
  percentage?: string | null;
}

export interface ArchitectHoursPreview {
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total: number;
  currency: string;
  tax_breakdown: ArchitectHoursTaxBreakdownEntry[];
  reverse_charge: boolean;
  requires_more_info: boolean;
  tax_id_rejected: boolean;
}

export interface UseArchitectHoursPreviewParams {
  token: string | null | undefined;
  target: ArchitectHoursPreviewTarget | null;
  address: ArchitectHoursPreviewAddress;
  // Gate the preview off entirely — e.g. before the address step has been submitted.
  enabled: boolean;
  // Discount voucher code currently applied, if any.
  voucherCode?: string | null;
  customerType?: 'individual' | 'business';
  businessName?: string;
  taxIdType?: string;
  taxIdValue?: string;
}

const DEBOUNCE_MS = 500;

// Returned when a request never resolves into a real number (network error, non-2xx) — so
// callers can rely on "preview is non-null" to mean "the round trip finished" instead of a
// skeleton spinning forever over a request that silently failed.
const UNRESOLVED_PREVIEW: ArchitectHoursPreview = {
  subtotal: 0,
  discount_amount: 0,
  tax_amount: 0,
  total: 0,
  currency: 'usd',
  tax_breakdown: [],
  reverse_charge: false,
  requires_more_info: true,
  tax_id_rejected: false,
};

export function useArchitectHoursPreview(params: UseArchitectHoursPreviewParams) {
  const [preview, setPreview] = useState<ArchitectHoursPreview | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const { token, target, address, enabled, voucherCode, customerType, businessName, taxIdType, taxIdValue } = params;
  const targetKey = JSON.stringify(target);
  const addressKey = JSON.stringify(address);
  const isBusiness = customerType === 'business';

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!enabled || !token || !target) {
      setPreview(null);
      return;
    }

    const thisRequestId = ++requestIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const body: Record<string, unknown> = { address };
        if ('sku' in target) body.service_sku = target.sku;
        else body.hours = target.hours;
        if (voucherCode) body.voucher_code = voucherCode;
        if (customerType) body.customer_type = customerType;
        if (isBusiness) {
          body.business_name = businessName;
          body.tax_id_type = taxIdType;
          body.tax_id_value = taxIdValue;
        }

        const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/billing/architect-hours/preview`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          if (requestIdRef.current === thisRequestId) setPreview(UNRESOLVED_PREVIEW);
          return;
        }
        const data: ArchitectHoursPreview = await response.json();
        if (requestIdRef.current === thisRequestId) setPreview(data);
      } catch {
        // Non-fatal: purchase still works without a preview, just shows the pre-tax total —
        // but still resolves the "are we still waiting" state so a skeleton doesn't spin forever.
        if (requestIdRef.current === thisRequestId) setPreview(UNRESOLVED_PREVIEW);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // targetKey/addressKey are the stable deps for the object params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, targetKey, addressKey, enabled, voucherCode, customerType, isBusiness, businessName, taxIdType, taxIdValue]);

  return { preview };
}
